import { ColDef, GetRowIdParams, ICellRendererParams } from 'ag-grid-community';
import 'ag-grid-enterprise';
import { AgGridReactProps } from 'ag-grid-react';
import { useMemo } from 'react';
import { StressIncrementType } from '../../../../../../Generated/Commodity-Management-Api';
import { StoneXGrid } from '../../../../../StoneX';
import { ExposureColumns, ExposureReportColumn, ExposureReportRow } from './Types';

interface ExposureReportTableProps {
    rows: ExposureReportRow[];
    columnsToShow: ExposureReportColumn[];
    activeStressIncrement: number | null;
    stressIncrementType: StressIncrementType;
}

function formatStressLabel(increment: number, type: StressIncrementType): string {
    const suffix = type === StressIncrementType.Percent ? '%' : '';
    const sign = increment > 0 ? '+' : '';
    return `${sign}${increment}${suffix}`;
}

function getGroupLabel(row: ExposureReportRow | undefined): string {
    if (!row) {
        return '';
    }
    switch (row.rowType) {
        case 'Counterparty':
            return row.counterpartyName ?? '';
        case 'Commodity':
            return row.commodity?.commodityName ?? '';
        case 'DealTicket':
            return row.dealTicket ?? (row.tradeId ? `Trade ${row.tradeId}` : '');
        case 'Position':
            return row.portfolioName ?? row.contractMonth ?? `Position ${row.positionId ?? ''}`;
        default:
            return '';
    }
}

export default function ExposureReportTable(props: ExposureReportTableProps) {
    const { columnsToShow, rows, activeStressIncrement, stressIncrementType } = props;

    const columns = useMemo<ColDef<ExposureReportRow>[]>(() => {
        const legacyAliases: { [key: string]: ExposureReportColumn } = {
            MarkToMarketValue: 'StressValue',
            MarkToMarketExposure: 'StressValue',
        };
        const seen = new Set<ExposureReportColumn>();
        const resolved = columnsToShow
            .map((column) => (legacyAliases[column as string] ?? column) as ExposureReportColumn)
            .filter((column) => {
                if (!ExposureColumns[column] || seen.has(column)) return false;
                seen.add(column);
                return true;
            });

        return resolved.map((column) => {
                const base = ExposureColumns[column] as ColDef<ExposureReportRow>;

                if (column === 'StressPrice' || column === 'StressValue') {
                    const stressLabel = activeStressIncrement !== null
                        ? ` (${formatStressLabel(activeStressIncrement, stressIncrementType)})`
                        : '';
                    const dictField: 'stressPrices' | 'stressValues' = column === 'StressPrice' ? 'stressPrices' : 'stressValues';
                    const currentIncrement = activeStressIncrement;

                    return {
                        ...base,
                        headerName: `${base.headerName}${stressLabel}`,
                        valueGetter: (params: any) => {
                            if (currentIncrement === null || !params.data) return null;
                            const dict = params.data[dictField] as { [key: string]: number | null } | undefined;
                            return dict?.[String(currentIncrement)] ?? null;
                        },
                    } as ColDef<ExposureReportRow>;
                }

                if (column === 'AvailableCredit') {
                    const currentIncrement = activeStressIncrement;
                    return {
                        ...base,
                        valueGetter: (params: any) => {
                            const data = params.data as ExposureReportRow | undefined;
                            if (!data) return null;
                            if (currentIncrement !== null) {
                                const stressed = data.availableCredits?.[String(currentIncrement)];
                                if (stressed !== undefined) return stressed;
                            }
                            return data.availableCredit ?? null;
                        },
                    } as ColDef<ExposureReportRow>;
                }

                return base;
            });
    }, [columnsToShow, activeStressIncrement, stressIncrementType]);

    const gridOptions = useMemo<AgGridReactProps<ExposureReportRow>>(() => ({
        treeData: true,
        getDataPath: (data) => data.path,
        groupDefaultExpanded: 1,
    }), []);

    function innerGroupRenderer(params: ICellRendererParams<ExposureReportRow>) {
        return getGroupLabel(params.data);
    }

    return (
        <StoneXGrid
            key={`exposure-grid-${activeStressIncrement ?? 'none'}`}
            columns={columns}
            rows={rows}
            gridOptions={gridOptions}
            groupColumnWidth={300}
            groupCellInnerRenderer={innerGroupRenderer}
            getRowId={(x: GetRowIdParams<ExposureReportRow>) => `${x.data.path.join('|')}|si:${activeStressIncrement ?? 'none'}`}
        />
    );
}
